import {
  AttendanceCode,
  AttendanceEntriesCounts,
  DEFAULT_DATE_FORMAT_FNS,
  JustificationCounts,
  SchoolYear,
  useGetAttendanceCodesQuery,
  useGetEnrollments,
  useGetRelationTrackingPointsAggregateQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';

import { toWeekRows } from '../common/attendanceGridData';
import {
  buildStudentTrackingPointsWeekGrid,
  TrackingPointsWeekGrid,
} from '../common/buildStudentTrackingPointsGrid';
import { getRegistrationValidityIntervals } from '../common/studentValidity';
import { AttendanceGridStudent } from '../common/types';

const TRACKING_POINTS_PAGE_SIZE = 5;

export type TrackingPointsWeekGridForView = Omit<TrackingPointsWeekGrid, 'rows'> & {
  rows: ReturnType<typeof toWeekRows>;
};

type UseAttendanceGridForStudentTrackingPointsResult = {
  weekGrids: TrackingPointsWeekGridForView[];
  codes: AttendanceCode[];
  counts: AttendanceEntriesCounts[];
  justificationCounts?: JustificationCounts;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  handleFetchNextPage: () => void;
};

export const useAttendanceGridForStudentTrackingPoints = (
  student: AttendanceGridStudent,
  selectedYear: SchoolYear | undefined,
  timetableStructureId?: string,
): UseAttendanceGridForStudentTrackingPointsResult => {
  const relationId = student.relation_id;
  const { schoolId = '' } = useAuth();

  const today = format(new Date(), DEFAULT_DATE_FORMAT_FNS);

  const { data: codes = [] } = useGetAttendanceCodesQuery(schoolId, { enabled: !!schoolId });

  const { data: enrollmentsData, isLoading: enrollmentsLoading } = useGetEnrollments(
    { schoolId, studentId: relationId },
    { enabled: !!schoolId && !!relationId, refetchOnMount: 'always' },
  );

  const dateFrom = selectedYear?.start ?? '';
  const dateTo = selectedYear && selectedYear.end < today ? selectedYear.end : today;
  const enabled = Boolean(relationId && schoolId && selectedYear && timetableStructureId);

  const {
    data,
    isLoading: aggregateLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetRelationTrackingPointsAggregateQuery(
    {
      relationId,
      school_id: schoolId,
      date_from: dateFrom,
      date_to: dateTo,
      page_size: TRACKING_POINTS_PAGE_SIZE,
      timetable_structure_id: timetableStructureId,
    },
    { enabled, refetchOnMount: 'always' },
  );

  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const validity = useMemo(
    () =>
      selectedYear
        ? getRegistrationValidityIntervals(enrollmentsData?.enrollments ?? [], selectedYear.id)
        : [],
    [enrollmentsData, selectedYear],
  );

  const { counts, justificationCounts } = useMemo(() => {
    const firstPage = data?.pages[0];
    if (!firstPage) return { counts: [], justificationCounts: undefined };

    return {
      counts: firstPage.total.session_counts.map((count, order) => ({
        attendance_code_id: count.attendance_code_id,
        count: count.count,
        order,
      })),
      justificationCounts: firstPage.total.justification_counts,
    };
  }, [data]);

  const weekGrids = useMemo<TrackingPointsWeekGridForView[]>(() => {
    if (!data?.pages) return [];

    const now = new Date();

    return data.pages.flatMap((page) =>
      page.weeks.map((week) => {
        const grid = buildStudentTrackingPointsWeekGrid({
          week,
          student,
          validity,
          today: now,
        });

        return {
          weekLabel: grid.weekLabel,
          columns: grid.columns,
          rows: toWeekRows(grid.rows),
        };
      }),
    );
  }, [data, student, validity]);

  return {
    weekGrids,
    codes,
    counts,
    justificationCounts,
    isLoading: enrollmentsLoading || aggregateLoading,
    isFetchingNextPage,
    handleFetchNextPage,
  };
};
